import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import VoiReviewDialog from "./VoiReviewDialog";
import { ShieldCheck, Send, Copy, Loader2, Eye, XCircle, Mail, MessageCircle, Link2, FileLock2 } from "lucide-react";

const STATUS = {
  PENDING_UPLOAD: ["Waiting for client", "#B45309"],
  WAITING_FOR_REVIEW: ["Ready to review", "var(--accent)"],
  APPROVED: ["Verified", "var(--moss)"],
  REJECTED: ["Rejected", "#6B7280"],
  EXPIRED: ["Expired", "#6B7280"],
};

export function VoiBadge({ status }) {
  const [label, color] = STATUS[status] || ["—", "#6B7280"];
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium"
          style={{ color, borderColor: color }} data-testid="voi-status-badge">{label}</span>
  );
}

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }

function Channels({ value, onChange, smsOk }) {
  const opts = [["email", "Email", Mail, true], ["sms", "SMS", MessageCircle, smsOk]];
  return (
    <div className="flex gap-3 text-[12px]">
      {opts.map(([k, l, Icon, ok]) => (
        <label key={k} className={`inline-flex items-center gap-1.5 ${ok ? "cursor-pointer" : "opacity-50"}`} title={ok ? "" : "SMS needs Twilio keys"}>
          <input type="checkbox" disabled={!ok} checked={value.includes(k)} data-testid={`voi-channel-${k}`}
                 onChange={(e) => onChange(e.target.checked ? [...value, k] : value.filter((x) => x !== k))} />
          <Icon size={12} /> {l}{!ok && " (not set up)"}
        </label>
      ))}
    </div>
  );
}

function reportDelivery(d) {
  Object.entries(d || {}).forEach(([ch, r]) => (r.ok ? toast.success(`Link sent by ${ch}`) : toast.error(`${ch}: ${r.error}`)));
}

function SessionRow({ s, onReview, onChanged, smsOk }) {
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState(["email"]);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const open = s.status === "PENDING_UPLOAD" || s.status === "WAITING_FOR_REVIEW";
  return (
    <li className="card p-4 space-y-2" data-testid={`voi-session-${s.session_id}`}>
      <div className="flex items-center justify-between gap-2">
        <VoiBadge status={s.status} />
        <span className="text-[11px] text-[var(--ink-3)] font-mono">{fmt(s.created_at)}</span>
      </div>
      <div className="text-[12px] text-[var(--ink-2)] space-y-0.5">
        {s.status === "PENDING_UPLOAD" && <div>Link expires {fmt(s.expires_at)}</div>}
        {s.status === "WAITING_FOR_REVIEW" && <div>Submitted {fmt(s.submitted_at)} · media auto-destroyed {fmt(s.media_expires_at)}</div>}
        {s.audit_reference && <div className="inline-flex items-center gap-1"><FileLock2 size={11} /> Audit <span className="font-mono" data-testid="voi-audit-ref">{s.audit_reference}</span> · media destroyed</div>}
      </div>
      {s.link && <Channels value={channels} onChange={setChannels} smsOk={smsOk} />}
      {open && (
        <div className="flex flex-wrap gap-2 pt-1">
          {s.status === "WAITING_FOR_REVIEW" && (
            <button className="btn-primary" onClick={() => onReview(s)} data-testid="voi-review-open"><Eye size={13} /> Review</button>
          )}
          {s.link && (
            <>
              <button className="btn-ghost" data-testid="voi-copy-link"
                onClick={() => navigator.clipboard.writeText(s.link).then(() => toast.success("Link copied")).catch(() => toast.error("Could not copy — select the link manually: " + s.link))}><Copy size={13} /> Copy link</button>
              <button className="btn-ghost" disabled={busy || !channels.length} data-testid="voi-resend"
                onClick={() => run(async () => { const r = await axios.post(`${API_BASE}/voi/sessions/${s.session_id}/resend`, { channels }); reportDelivery(r.data.delivery); })}>
                <Send size={13} /> Resend</button>
            </>
          )}
          <button className="btn-ghost text-[var(--accent)]" disabled={busy} data-testid="voi-revoke"
            onClick={() => window.confirm("Cancel this verification and destroy any uploaded media?") &&
              run(async () => { await axios.post(`${API_BASE}/voi/sessions/${s.session_id}/revoke`); toast.success("Verification cancelled"); onChanged(); })}>
            <XCircle size={13} /> Cancel</button>
        </div>
      )}
    </li>
  );
}

export default function VoiPanel({ clientId, client }) {
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [channels, setChannels] = useState(["email"]);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([axios.get(`${API_BASE}/voi/config`), axios.get(`${API_BASE}/voi/clients/${clientId}`)]);
    setConfig(c.data); setSessions(s.data.sessions);
  }, [clientId]);
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/voi/sessions`, { client_id: clientId, channels });
      reportDelivery(r.data.delivery);
      if (!channels.length) await navigator.clipboard.writeText(r.data.link).then(() => toast.success("Link copied")).catch(() => {});
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const openReview = async (s) => {
    try { setConfig((await axios.get(`${API_BASE}/voi/config`)).data); } catch { /* keep cached config */ }
    setReview(s);
  };

  if (!sessions) return <div className="p-5"><Loader2 className="animate-spin text-[var(--ink-3)]" size={16} /></div>;
  const hasOpen = sessions.some((s) => s.status === "PENDING_UPLOAD" || s.status === "WAITING_FOR_REVIEW");
  const verified = sessions.find((s) => s.status === "APPROVED");
  return (
    <div className="p-5 space-y-4" data-testid="voi-panel">
      <div className="flex items-start gap-3">
        <ShieldCheck size={18} className={verified ? "text-[var(--moss)]" : "text-[var(--ink-3)]"} />
        <div className="flex-1">
          <div className="text-[14px] font-medium text-[var(--ink)]" data-testid="voi-summary">
            {verified ? `Identity verified ${fmt(verified.completed_at)}` : "Identity not yet verified"}
          </div>
          <div className="text-[12px] text-[var(--ink-3)]">Visual + Source ATO · photo ID & selfie video, then two ATO data points. Media destroyed after review.</div>
        </div>
      </div>
      {!hasOpen && (
        <div className="card p-4 space-y-3" data-testid="voi-start-card">
          <div className="overline">Send a verification link</div>
          <Channels value={channels} onChange={setChannels} smsOk={config?.sms_configured} />
          <div className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1"><Link2 size={11} /> Single-use link, valid {config?.link_ttl_hours}h. Leave both unticked to just copy the link.</div>
          <button className="btn-primary" onClick={start} disabled={busy} data-testid="voi-start">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {channels.length ? "Send link" : "Create & copy link"}
          </button>
        </div>
      )}
      <ul className="space-y-2" data-testid="voi-session-list">
        {sessions.map((s) => <SessionRow key={s.session_id} s={s} onReview={openReview} onChanged={load} smsOk={config?.sms_configured} />)}
      </ul>
      {review && config && (
        <VoiReviewDialog session={review} client={client} config={config}
          onClose={() => setReview(null)} onDone={() => { setReview(null); load(); }} />
      )}
    </div>
  );
}
